import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const PRIMARY     = '#5d4037';
const PRIMARY_MID = '#795548';
const PRIMARY_LT  = '#efebe9';

const fmt = (n) =>
  `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt2Date = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const iconBtn = {
  color: 'rgba(255,255,255,0.85)',
  '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' },
  borderRadius: '6px', p: '4px',
};
const th  = { padding: '8px 10px', textAlign: 'left',  fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', borderBottom: '2px solid rgba(255,255,255,0.2)' };
const td  = { padding: '6px 10px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' };
const tdR = { ...td, textAlign: 'right' };
const tdC = { ...td, textAlign: 'center' };

const ExpenseReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [fetched, setFetched]       = useState(false);
  const [search, setSearch]         = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(today));

  const fetchReport = async (cId = companyId, bId = branchId, tok = token) => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    if (!cId || !bId || !tok) { setError('Authentication data missing'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetchData(
        'AccountsReportApi',
        `/ExpenseAdvancedReport/${cId}/${bId}/${fromDate}/${toDate}`,
        tok
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error('ExpenseReport API error:', err);
      setError('Failed to fetch expense report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport(companyId, branchId, token);
  }, [companyId, branchId, token]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.MainHeadName || '').toLowerCase().includes(q) ||
      (r.LedgerName   || '').toLowerCase().includes(q) ||
      (r.Source       || '').toString().toLowerCase().includes(q)
    );
  }, [data, search]);

  const totalAmount = filtered.reduce((s, r) => s + (parseFloat(r.Amount) || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'ExpenseReport', 0);
    if (!filtered.length) return;
    const cols = ['#', 'Bill Date', 'Main Head', 'Ledger', 'Amount', 'Source'];
    const rows = filtered.map((r, i) => [
      i + 1,
      fmtDate(r.BillDate),
      r.MainHeadName || '',
      r.LedgerName   || '',
      parseFloat(r.Amount) || 0,
      r.Source || '',
    ]);

      await exportStyledExcel({
          reportTitle: 'EXPENSE REPORT',
          sheetName: 'Expense Report',
          fileName: `Report.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: cols,
          rows,
          totalsRow: ['', '', '', 'Total', totalAmount, ''],
          columnWidths: [5,14,22,28,15,15],
      });
  };

  // Group by MainHeadName
  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const mh = r.MainHeadName || 'Other';
      if (!map[mh]) map[mh] = [];
      map[mh].push(r);
    });
    return map;
  }, [filtered]);

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading expense report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'linear-gradient(135deg, #5d4037 0%, #795548 100%)', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Expense Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search head, ledger..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 200, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate}   onChange={e => setToDate(e.target.value)}   style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={() => fetchReport()} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 700 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Bill Date</th>
                <th style={th}>Main Head</th>
                <th style={th}>Ledger</th>
                <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                <th style={th}>Source</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Click refresh to load report'}
                  </td>
                </tr>
              ) : (
                (() => {
                  let rowIdx = 0;
                  return Object.entries(grouped).map(([mh, rows]) => {
                    const mhTotal = rows.reduce((s, r) => s + (parseFloat(r.Amount) || 0), 0);
                    return [
                      // Main Head group header
                      <tr key={`mh-${mh}`} style={{ background: '#e8eaf6' }}>
                        <td colSpan={6} style={{ ...td, fontWeight: 700, color: PRIMARY, fontSize: 12, padding: '5px 10px', borderTop: '2px solid #c5cae9', borderBottom: '1px solid #c5cae9' }}>
                          {mh}
                        </td>
                      </tr>,
                      // Data rows
                      ...rows.map((r) => {
                        const i = rowIdx++;
                        return (
                          <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ ...td, color: '#94a3b8' }}>{i + 1}</td>
                            <td style={{ ...td, color: '#64748b' }}>{fmtDate(r.BillDate)}</td>
                            <td style={{ ...td, fontWeight: 600, color: '#1e293b' }}>{r.MainHeadName || '-'}</td>
                            <td style={{ ...td, color: PRIMARY }}>{r.LedgerName || '-'}</td>
                            <td style={{ ...tdR, color: '#dc2626', fontWeight: 600 }}>{fmt(r.Amount)}</td>
                            <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{r.Source || '-'}</td>
                          </tr>
                        );
                      }),
                      // Group subtotal
                      <tr key={`mh-total-${mh}`} style={{ background: '#e8eaf6', borderTop: '1px solid #c5cae9' }}>
                        <td colSpan={4} style={{ ...td, textAlign: 'right', fontWeight: 700, color: PRIMARY, fontSize: 11 }}>Subtotal — {mh}</td>
                        <td style={{ ...tdR, fontWeight: 700, color: '#dc2626' }}>{fmt(mhTotal)}</td>
                        <td style={td}></td>
                      </tr>,
                    ];
                  });
                })()
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_MID, color: '#fff', fontWeight: 700 }}>
                  <td colSpan={4} style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#fff' }}>Grand Total</td>
                  <td style={{ ...tdR, fontWeight: 700, color: '#fca5a5' }}>{fmt(totalAmount)}</td>
                  <td style={td}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExpenseReport;
